import { campaignAcceptsDonations, campaignEnded } from '../../toll/campaignRules';
import { roundMoney } from '../../toll/money';
import { Button, Checkbox, DatePicker, Form, Input, message } from "antd";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import PageBox from "../../layout/PageBox";
import { MyColor } from "../../config/myStyle";
import { afd_donation_now_button_icon, afd_payme_qrcode, meal_box_slogan_frame, payme_icon } from "../../imgs";
import { campaignStore, campaignThemeStore, donationStore, paymentStore, userStore } from "../../stores";
import { Coupon } from "../../components/Coupon";
import { MyRadio } from "../../components/MyRadio";
import { MyInput } from "../../components/MyInput";
import { MySelect } from "../../components/MySelect";
import { FormRequiredMark } from "../../components/FormRequiredMark";
import { MyUpload } from "../../components/MyUpload";
import api, { PostAssetUploadParameters } from "../../api";
import config from "../../config";
import { useTranslation } from "react-i18next";
import { isReceiptEligible, receiptPayload } from "../../toll/donationReceipt";
import { getCampaignThemeImages } from "../../toll/campaignThemeVisual";

const DONATION_OPTIONS = [
    { meals: 10, amount: 150 },
    { meals: 20, amount: 300 },
    { meals: 30, amount: 450 },
    { meals: 40, amount: 600 },
];

const sectionTitleStyle: CSSProperties = {
    color: MyColor.blue,
    fontSize: 16,
    fontWeight: 700,
};

type UploadFileValue = {
    url: string;
    size: number;
    name: string;
    type: string;
};

const PostAssetUpload = async (params: PostAssetUploadParameters) => {
    const res = await api.PostAssetUpload(params);
    if (res.kind !== "ok") {
        throw new Error(res.message);
    }
    return res.data;
};

const CampaignDonation = observer(() => {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [selectedIdx, setSelectedIdx] = useState(2);
    const [otherAmount, setOtherAmount] = useState("");
    const [payMethod, setPayMethod] = useState<"payme" | "bank" | "cheque">("payme");
    const [amountVisibility, setAmountVisibility] = useState<"public" | "hidden">("public");
    const [submitting, setSubmitting] = useState(false);
    const [receiptOption, setReceiptOption] = useState<"no_thanks" | "e_receipt">("no_thanks");
    const [isAnonymous, setIsAnonymous] = useState(false);
    const salutationOptions = [
        // 捐款接口要求固定枚举，显示文案与提交值需要分开。
        { label: t("campaign_donation.salutation_mr"), value: "mr" },
        { label: t("campaign_donation.salutation_miss"), value: "miss" },
        { label: t("campaign_donation.salutation_ms"), value: "ms" },
    ];

    const campaign = campaignStore.campaignMap.get(campaignId ?? "");
    const themeName = campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.nameObj?.[userStore.language ?? "zh-HK"]
        ?? campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.name;
    const themeImages = getCampaignThemeImages(campaign, themeName);
    const campaignStatus = campaignStore.campaignStatusMap.get(campaignId ?? "");

    const donationAmount = useMemo(
        () => (selectedIdx >= 0 ? DONATION_OPTIONS[selectedIdx].amount : roundMoney(otherAmount)),
        [otherAmount, selectedIdx]
    );

    const receiptEligible = isReceiptEligible(donationAmount);
    const effectiveReceiptOption = receiptEligible ? receiptOption : "no_thanks";
    const showDonorInfo = effectiveReceiptOption === "e_receipt";
    // 调低金额即清除收据选择；渲染和请求体也使用同一资格条件，避免异步空窗。
    useEffect(() => {
        if (!receiptEligible) setReceiptOption("no_thanks");
    }, [receiptEligible]);

    const donateDisabled = !donationAmount || donationAmount < 1 || !campaignAcceptsDonations(campaign);

    const handleSelect = (idx: number) => {
        setSelectedIdx(idx);
        setOtherAmount("");
    };

    const handleOtherFocus = () => setSelectedIdx(-1);

    const createDonationPayload = async (values: any) => {
        const paymentSlip: UploadFileValue | undefined = values.paymentSlip?.[0];
        const donationAssets: { assetId: string }[] = [];

        if (payMethod === "bank" && paymentSlip) {
            const uploadRes = await PostAssetUpload({ files: paymentSlip });
            donationAssets.push({ assetId: uploadRes.id });
        }

        return {
            amount: donationAmount,
            campaignId: campaignId ?? "",
            currency: "HKD" as const,
            donationAssets: donationAssets.length ? donationAssets : undefined,
            ...receiptPayload(values, showDonorInfo, isAnonymous),
            // 公开/隐藏现在直接走接口字段，不再把状态塞进 remark。
            isAmountHidden: amountVisibility === "hidden",
            message: values.message?.trim() || undefined,
            paymentMethod: payMethod,
            receiptOption: effectiveReceiptOption,
            settlementDate: payMethod === "bank" && values.settlementDate
                ? moment(values.settlementDate).startOf("day").toISOString()
                : undefined,
            chequeNumber: payMethod === "cheque" ? values.chequeNumber?.trim() || undefined : undefined,
        };
    };

    const onSubmit = async (values: any) => {
        if (!campaignId || submitting) return;
        if (!campaignAcceptsDonations(campaign)) { message.error(t("campaign_rules.unavailable")); return; }
        if (donateDisabled) {
            message.error(t("campaign_donation.enter_valid_amount"));
            return;
        }

        try {
            setSubmitting(true);
            const payload = await createDonationPayload(values);
            donationStore.resetCreateDonation();
            await donationStore.createDonation(payload);

            if (!donationStore.createDonationSuccess || !donationStore.createDonationData?.id) {
                return;
            }

            const created = donationStore.createDonationData;
            // 每筆捐款分開保存查詢憑證，回跳網址不包含令牌。
            localStorage.setItem(`fa_donation_${created.id}`, created.authToken);
            const resultPath = `/donation/${campaignId}/amount/result/?donationId=${encodeURIComponent(created.id)}`;
            if (payMethod === "payme") {
                localStorage.setItem("donation_authToken", donationStore.createDonationData.authToken);
                const appSuccessCallback = `${config.REACT_APP_HOST.replace(/\/$/, "")}${resultPath}`;
                paymentStore.resetCreatePaymePayment();
                await paymentStore.createPaymePayment({
                    donationId: donationStore.createDonationData.id,
                    appSuccessCallback,
                    appFailCallback: appSuccessCallback,
                    authToken: donationStore.createDonationData.authToken,
                });

                // PayMe 本轮严格按 appLink 跳转，且跳转前不清空支付结果，避免链接状态被提前重置。
                const paymeAppLink = paymentStore.createPaymePaymentData?.appLink;
                if (paymentStore.createPaymePaymentSuccess && paymeAppLink) {
                    donationStore.resetCreateDonation();
                    window.location.assign(paymeAppLink);
                    return;
                }
            } else {
                donationStore.resetCreateDonation();
                navigate(resultPath);
                return;
            }

            message.error(t("campaign_donation.payment_redirect_failed"));
        } catch (error: any) {
            message.error(error.message || t("campaign_donation.submit_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        campaignStore.getCampaign(campaignId ?? "");
    }, [campaignId]);

    return (
        <PageBox spinning={campaignStatus?.loading || donationStore.createDonationStatus.loading || paymentStore.createPaymePaymentStatus.loading || submitting}>
            <div className="w-full">
                <div style={{ width: "100%", aspectRatio: "16/9", marginTop: 10 }}>
                    <img
                        src={themeImages.cover}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                        alt=""
                    />
                </div>

                <div className="px-[12px] pb-[40px] pt-[20px]">
                    {/* 图文作为整体居中；留足左右内边距，窄屏仅文字自然换行。 */}
                    <div className="mx-auto flex w-full max-w-[720px] items-center justify-center gap-[20px] rounded-[20px] bg-[#FFF8D9] px-[20px] py-[20px] sm:gap-[24px] sm:px-[32px]" style={{ color: MyColor.blue }}>
                        <div className="donation-meal-icon"><img src={meal_box_slogan_frame} alt="" /></div>
                        <p className="min-w-0 max-w-[510px] text-[18px] font-semibold leading-[1.6]">{t("campaign_donation.meal_message")}</p>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={FormRequiredMark}
                        onFinish={onSubmit}
                        initialValues={{
                            salutation: "mr",
                        }}
                    >
                        <div className="mt-[28px] rounded-[24px] bg-[white] px-[14px] py-[20px] shadow-[0_8px_20px_rgba(0,0,0,0.04)]">
                            <div className="text-center text-[24px] font-[700]" style={{ color: MyColor.blue }}>
                                {t("campaign_donation.select_donation_amount")}
                            </div>

                            <div className="mt-[16px] grid grid-cols-2 gap-x-[16px] gap-y-[16px]">
                                {DONATION_OPTIONS.map((item, index) => (
                                    <Coupon
                                        key={index}
                                        select={selectedIdx === index}
                                        onSelect={() => handleSelect(index)}
                                        meals={item.meals}
                                        amount={item.amount}
                                    />
                                ))}
                            </div>

                            <div className="mt-[14px] flex items-center space-x-[10px]">
                                <MyRadio onSelect={handleOtherFocus} select={selectedIdx === -1} />
                                <span style={{ color: MyColor.blue, fontWeight: 600, fontSize: 17 }}>
                                    {t("campaign_donation.other_hk")}
                                </span>
                                <Input
                                    style={{
                                        flex: 1,
                                        border: `2px solid ${selectedIdx === -1 ? MyColor.red : MyColor.blue}`,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        color: MyColor.blue,
                                        height: 40,
                                    }}
                                    type="number"
                                    aria-label={t("campaign_donation.other_hk")}
                                    value={otherAmount}
                                    onFocus={handleOtherFocus}
                                    onBlur={() => { if (otherAmount && roundMoney(otherAmount)) setOtherAmount(roundMoney(otherAmount).toFixed(2)); }}
                                    onChange={e => {
                                        setOtherAmount(e.target.value);
                                        setSelectedIdx(-1);
                                    }}
                                />
                            </div>

                            <div className="mt-[22px] rounded-[22px] bg-[#FCFBF6] p-[16px]">
                                <div className="text-[16px] font-[700]" style={{ color: MyColor.blue }}>
                                    {t("campaign_donation.amount_visibility")}
                                </div>
                                <div className="mt-[12px] grid grid-cols-2 gap-[10px]">
                                    {(["public", "hidden"] as const).map(option => (
                                        <button
                                            key={option}
                                            type="button"
                                            className="rounded-[16px] border px-[14px] py-[12px] text-[15px] font-[600]"
                                            style={{
                                                borderColor: amountVisibility === option ? MyColor.red : "rgba(0,0,0,0.18)",
                                                background: amountVisibility === option ? "#FFF1F7" : "white",
                                                color: amountVisibility === option ? MyColor.red : MyColor.blue,
                                            }}
                                            onClick={() => setAmountVisibility(option)}
                                        >
                                            {t(`campaign_donation.amount_visibility_${option}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <Form.Item
                                className="mb-[0] mt-[18px]"
                                label={t("campaign_donation.encourage_message")}
                                name="message"
                            >
                                <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 6 }}
                                    maxLength={100}
                                    placeholder={t("campaign_donation.encourage_message_placeholder")}
                                />
                            </Form.Item>
                        </div>

                        <div className="mt-[18px] rounded-[24px] bg-[white] px-[14px] py-[20px] shadow-[0_8px_20px_rgba(0,0,0,0.04)]">
                            <div className="border-b border-[rgba(0,0,0,0.14)] pb-[16px]">
                                <div style={sectionTitleStyle}>
                                    {t("campaign_donation.payment_methods")}
                                </div>
                                <div className="mt-[12px] space-y-[8px]">
                                    {([
                                        { key: "payme", label: t("campaign_donation.payme") },
                                        { key: "bank", label: t("campaign_donation.bank_transfer") },
                                        { key: "cheque", label: t("campaign_donation.cheque") },
                                    ] as const).map(item => (
                                        <button
                                            key={item.key}
                                            type="button"
                                            aria-label={item.label}
                                            aria-pressed={payMethod === item.key}
                                            className="flex w-full items-center justify-between rounded-[14px] border px-[14px] py-[10px]"
                                            style={{
                                                borderColor: payMethod === item.key ? MyColor.blue : "rgba(0,0,0,0.12)",
                                                background: "white",
                                            }}
                                            onClick={() => setPayMethod(item.key)}
                                        >
                                            <div className="flex items-center text-[16px] font-[600]" style={{ color: MyColor.blue }}>
                                                {item.key === "payme" && (
                                                    <img src={payme_icon} alt="" className="mr-[10px] h-[28px]" />
                                                )}
                                                {item.label}
                                            </div>
                                            <MyRadio select={payMethod === item.key} type="hook" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {payMethod === "bank" && (
                                <div className="mt-[18px] rounded-[18px] bg-[#F4F4F7] p-[16px]">
                                    <div className="text-[14px] leading-[1.7] text-[rgba(0,0,0,0.72)]">
                                        <div><b>{t("campaign_donation.bank_account_name")}</b>: Bo Charity Foundation Limited</div>
                                        <div><b>{t("campaign_donation.bank_name_code")}</b>: Hong Kong &amp; Shanghai Banking Corporation (004)</div>
                                        <div><b>{t("campaign_donation.bank_account_no")}</b>: 868-627715-001</div>
                                        <div><b>{t("campaign_donation.fps_proxy_id")}</b>: 119766194</div>
                                    </div>
                                    <div className="mt-[16px] flex justify-center">
                                        <img src={afd_payme_qrcode} alt="" className="w-[210px] max-w-full rounded-[12px] bg-[white] p-[10px]" />
                                    </div>

                                    <Form.Item
                                        className="mt-[18px] mb-[0]"
                                        label={t("campaign_donation.settlement_date")}
                                        name="settlementDate"
                                        rules={[{ required: true, message: t("campaign_donation.please_select_settlement_date") }]}
                                    >
                                        <DatePicker className="h-[46px] w-full" />
                                    </Form.Item>

                                    <Form.Item
                                        className="mt-[18px] mb-[0]"
                                        label={t("campaign_donation.upload_payment_slip")}
                                        name="paymentSlip"
                                        rules={[{ required: true, message: t("campaign_donation.please_upload_payment_slip") }]}
                                    >
                                        <MyUpload
                                            crop={false}
                                            maxCount={1}
                                            maxSizeMB={1}
                                            buttonText={t("campaign_donation.choose_file")}
                                            tipHtml={t("campaign_donation.payment_slip_upload_tip")}
                                        />
                                    </Form.Item>
                                </div>
                            )}

                            {payMethod === "cheque" && (
                                <div className="mt-[18px] rounded-[18px] bg-[#F4F4F7] p-[16px] text-[14px] leading-[1.7] text-[rgba(0,0,0,0.72)]">
                                    <div dangerouslySetInnerHTML={{ __html: t("campaign_donation.cheque_instruction") }} />
                                    <Form.Item
                                        className="mt-[18px] mb-[0]"
                                        label={t("campaign_donation.cheque_no")}
                                        name="chequeNumber"
                                        rules={[{ required: true, message: t("campaign_donation.please_enter_cheque_no") }]}
                                    >
                                        <MyInput placeholder={t("campaign_donation.cheque_no")} />
                                    </Form.Item>
                                </div>
                            )}

                            {/* 是否需要收据放在付款方式之后，匿名不会关闭此选项。 */}
                                {receiptEligible ? <div className="mt-[18px]">
                                    <div style={sectionTitleStyle}>
                                        {t("campaign_donation.receipt_option")}
                                    </div>
                                    <div className="mt-[12px] space-y-[8px]">
                                        {([
                                            { key: "no_thanks", label: t("campaign_donation.no_thanks") },
                                            { key: "e_receipt", label: t("campaign_donation.e_receipt") },
                                        ] as const).map(item => (
                                            <button
                                                key={item.key}
                                                type="button"
                                                aria-label={item.label}
                                                aria-pressed={receiptOption === item.key}
                                                className="flex w-full items-center justify-between rounded-[14px] border px-[14px] py-[10px]"
                                                style={{
                                                    borderColor: receiptOption === item.key ? MyColor.blue : "rgba(0,0,0,0.12)",
                                                    background: "white",
                                                }}
                                                onClick={() => setReceiptOption(item.key)}
                                            >
                                                <div className="flex items-center text-[16px] font-[600]" style={{ color: MyColor.blue }}>
                                                    {item.label}
                                                </div>
                                                <MyRadio select={receiptOption === item.key} type="hook" />
                                            </button>
                                        ))}
                                    </div>
                                </div> : <p className="mt-[18px] text-[14px]" role="status">{t("campaign_donation.receipt_eligibility")}</p>}

                            <div className={`mt-[24px] ${payMethod !== "payme" ? "border-t border-[rgba(0,0,0,0.14)]" : ""} pt-[18px]`}>
                                <div style={sectionTitleStyle}>
                                    {t("campaign_donation.donor_information")}
                                </div>

                                <div
                                    role="button"
                                    tabIndex={0}
                                    className="mt-[12px] flex w-full items-center justify-between rounded-[14px] border px-[14px] py-[10px]"
                                    style={{
                                        borderColor: isAnonymous ? MyColor.blue : "rgba(0,0,0,0.12)",
                                        background: "white",
                                    }}
                                    onClick={() => setIsAnonymous(!isAnonymous)}
                                    onKeyDown={event => {
                                        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                                            event.preventDefault();
                                            setIsAnonymous(!isAnonymous);
                                        }
                                    }}
                                >
                                    <span className="text-[16px] font-[600]" style={{ color: MyColor.blue }}>
                                        {t("campaign_donation.anonymous_donations")}
                                    </span>
                                    <Checkbox
                                        aria-label={t("campaign_donation.anonymous_donations")}
                                        checked={isAnonymous}
                                        onClick={event => event.stopPropagation()}
                                        onChange={event => setIsAnonymous(event.target.checked)}
                                    />
                                </div>

                                <p className="mt-[10px] text-[14px] leading-[1.6]">{t("campaign_donation.anonymous_note")}</p>
                                {!isAnonymous && (
                            <Form.Item
                                className="mb-[0] mt-[18px]"
                                label={t("campaign_donation.display_name")}
                                name="displayName" preserve={false}
                            >
                                <MyInput
                                    maxLength={80}
                                    placeholder={t("campaign_donation.display_name_placeholder")}
                                />
                            </Form.Item>

                                )}

                                {showDonorInfo && (
                                    <>
                                        <Form.Item
                                            className="mt-[18px] mb-[0]"
                                            label={t("campaign_donation.salutation")}
                                            name="salutation"
                                            rules={[{ required: true, message: t("campaign_donation.please_select_salutation") }]}
                                        >
                                            <MySelect options={salutationOptions} placeholder={t("campaign_donation.please_select_salutation")} />
                                        </Form.Item>

                                        <Form.Item
                                            className="mt-[18px] mb-[0]"
                                            label={t("campaign_donation.first_name")}
                                            name="firstName" preserve={false}
                                            rules={[{ required: true, message: t("campaign_donation.please_enter_first_name") }]}
                                        >
                                            <MyInput maxLength={50} placeholder={t("campaign_donation.first_name")} />
                                        </Form.Item>

                                        <Form.Item
                                            className="mt-[18px] mb-[0]"
                                            label={t("campaign_donation.last_name")}
                                            name="lastName" preserve={false}
                                            rules={[{ required: true, message: t("campaign_donation.please_enter_last_name") }]}
                                        >
                                            <MyInput maxLength={50} placeholder={t("campaign_donation.last_name")} />
                                        </Form.Item>

                                        <Form.Item className="mt-[18px] mb-[0]" label={t("campaign_donation.receipt_title")} name="receiptTitle" preserve={false}
                                            rules={[{ required: true, whitespace: true, message: t("campaign_donation.please_enter_receipt_title") }]}>
                                            <MyInput maxLength={200} placeholder={t("campaign_donation.receipt_title")} />
                                        </Form.Item>

                                        <Form.Item
                                            className="mt-[18px] mb-[0]"
                                            label={t("campaign_donation.email_address")}
                                            name="email" preserve={false}
                                            rules={[
                                                { required: true, message: t("campaign_donation.please_enter_email") },
                                                { type: "email", message: t("campaign_donation.invalid_email") },
                                            ]}
                                        >
                                            <MyInput maxLength={100} placeholder={t("campaign_donation.email_address")} />
                                        </Form.Item>

                                        <Form.Item
                                            className="mt-[18px] mb-[0]"
                                            label={t("campaign_donation.mobile_number")}
                                            name="mobile" preserve={false}
                                            rules={[
                                                { required: true, message: t("campaign_donation.please_enter_mobile") },
                                                { pattern: /^\d{6,16}$/, message: t("campaign_donation.invalid_mobile") },
                                            ]}
                                        >
                                            <Input
                                                addonBefore={<div className="px-[2px]">852</div>}
                                                placeholder={t("campaign_donation.mobile_number")}
                                                maxLength={16}
                                            />
                                        </Form.Item>
                                    </>
                                )}
                            </div>

                            <div className="mt-[18px] text-[13px] leading-[1.7] text-[rgba(0,0,0,0.62)]">
                                <div dangerouslySetInnerHTML={{ __html: t("campaign_donation.personal_details_note") }} />
                                <div className="mt-[6px]" dangerouslySetInnerHTML={{ __html: t("campaign_donation.receipt_note") }} />
                            </div>

                            <Button
                                type="primary"
                                htmlType="submit"
                                className="mt-[22px] flex h-[54px] w-full items-center justify-center rounded-[999px] !border-none"
                                style={{
                                    background: donateDisabled ? "#e3e3e3" : MyColor.red,
                                    color: donateDisabled ? "#555" : "white",
                                    fontSize: 20,
                                    fontWeight: 700,
                                    opacity: 1,
                                }}
                                disabled={donateDisabled}
                            >
                                <img
                                    src={afd_donation_now_button_icon}
                                    alt=""
                                    style={{ height: 32, marginRight: 10 }}
                                />
                                {!campaignAcceptsDonations(campaign) ? t(campaignEnded(campaign) ? "campaign_rules.ended" : "campaign_rules.unavailable") : payMethod === "payme" ? t("campaign_donation.donate_now") : t("campaign_donation.submit")}
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </PageBox>
    );
});

export default CampaignDonation;
